import { UPLOAD_IMAGE_MAX_BYTES } from '@platform/contracts';

/** Tipos que se pueden elegir (la API vuelve a validar por los bytes). */
export const IMAGE_ACCEPT = 'image/png,image/jpeg,image/webp,image/avif,image/gif,image/x-icon';

function readAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      resolve(typeof r.result === 'string' ? r.result : '');
    };
    r.onerror = () => {
      reject(new Error('No se pudo leer el archivo'));
    };
    r.readAsDataURL(blob);
  });
}

/**
 * Prepara una imagen para subir: la reduce a `max` px de lado (una foto de móvil pesa MB y se
 * muestra a 32 px) y la codifica — JPEG para fotos, PNG para logos (conserva la transparencia).
 * Si el navegador no la puede decodificar (p. ej. un .ico), se manda tal cual y valida la API.
 * Devuelve el data URL en base64.
 */
export async function prepareImage(
  file: File,
  { max = 256, kind = 'logo' }: { max?: number; kind?: 'logo' | 'photo' } = {},
): Promise<string> {
  if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') {
    throw new Error('Elige una imagen PNG, JPG, WebP, GIF o ICO');
  }
  let out: Blob = file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(bitmap, 0, 0, w, h);
      const type = kind === 'photo' ? 'image/jpeg' : 'image/png';
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, type, 0.88);
      });
      if (blob) out = blob;
    }
    bitmap.close();
  } catch {
    // Formato que el navegador no decodifica: va el original.
  }
  if (out.size > UPLOAD_IMAGE_MAX_BYTES) {
    throw new Error('La imagen pesa demasiado (máximo 512 KB)');
  }
  return readAsDataUrl(out);
}
